// src/pages/Home.jsx
// The site's cover page.
// Five sections matching Module 7's index.html:
//   1. Cover hero (headline + byline)
//   2. Colophon (author bio)
//   3. The Stack (tools of the trade)
//   4. Field Notes (selected projects)
//   5. Dispatches preview (recent essays)
//
// Content is static. The /blogs page will fetch dynamically from the backend;
// here we keep an editorial curation of three featured essays so the homepage
// reads like a magazine cover.
//
// Internal links use <Link> (React Router) to preserve SPA navigation.
// External / placeholder links keep <a> tags.
import { Link } from "react-router-dom";

export default function Home() {
  return (
    <>
      {/* ===========================
          COVER HERO
          =========================== */}
      <section className="max-w-wide mx-auto px-6 pt-20 pb-24 md:pt-32 md:pb-40">
        <p className="label-mono mb-8">The Cover</p>
        <h1 className="font-display text-5xl md:text-display leading-tight md:leading-[1.05] font-medium tracking-tight mb-10 max-w-4xl">
          A field journal of full-stack work,{" "}
          <em className="italic">decentralized systems</em>, and adjacent
          practice.
        </h1>
        <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-6">
          <span className="label-mono">By Sandra Hershey-Zagrans</span>
          <span className="hidden md:inline label-mono text-ink-30">·</span>
          <span className="label-mono">Powell, Ohio</span>
          <span className="hidden md:inline label-mono text-ink-30">·</span>
          <span className="label-mono">Est. 2026</span>
        </div>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* ===========================
          COLOPHON
          =========================== */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-3">
            <p className="label-mono">Colophon</p>
            <h2 className="font-display text-h2 leading-tight font-medium mt-3">
              About the author
            </h2>
          </div>
          <div className="md:col-span-9 max-w-prose">
            <p className="font-display italic text-lede leading-relaxed text-ink mb-6">
              Sandra Hershey-Zagrans (<em>Alee</em>) is a full-stack developer
              in training, writing from a small operation in central Ohio.
            </p>
            <p className="text-body leading-relaxed text-ink-70 mb-5">
              The work here sits at the intersection of three older threads:
              nearly two decades in voice and network engineering, a working
              background in cybersecurity (CCNA), and a long-running interest in
              how systems (technical, civic, monetary) actually hold together.
              The bootcamp work is the through-line that makes those threads
              buildable.
            </p>
            <p className="text-body leading-relaxed text-ink-70">
              Recent preoccupations: PostgreSQL schema design, the EVM,
              fermentation, the Theory of Constraints, and what it would take to
              run a small manufacturing operation that respects both the math
              and the people doing the work.
            </p>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* ===========================
          THE STACK
          =========================== */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-3">
            <p className="label-mono">The Stack</p>
            <h2 className="font-display text-h2 leading-tight font-medium mt-3">
              Tools of the trade
            </h2>
          </div>
          <div className="md:col-span-9">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8">
              <div>
                <dt className="label-mono mb-3">Frontend</dt>
                <dd className="text-body text-ink">
                  HTML, CSS, TailwindCSS, JavaScript, React
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Backend</dt>
                <dd className="text-body text-ink">
                  Node.js, Express, REST APIs
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Data</dt>
                <dd className="text-body text-ink">
                  PostgreSQL, MongoDB, parameterized SQL
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Web3 (in progress)</dt>
                <dd className="text-body text-ink">
                  Solidity, EVM fundamentals, Ethereum tooling
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Operations</dt>
                <dd className="text-body text-ink">
                  Linux, Docker, Git, CI workflows, networking
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Adjacent practice</dt>
                <dd className="text-body text-ink">
                  Technical writing, dev education video, systems thinking
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* ===========================
          FIELD NOTES (projects preview)
          =========================== */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-mono">Field Notes</p>
            <h2 className="font-display text-h2 leading-tight font-medium mt-3">
              Selected work
            </h2>
          </div>
          <Link
            to="/projects"
            className="label-mono hover:text-ink transition-colors hidden md:inline"
          >
            All projects →
          </Link>
        </div>

        <div className="border-t border-ink-20">
          {/* Project 1 */}
          <article className="border-b border-ink-20 py-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline">
              <p className="label-mono md:col-span-2">No. 01 · 2026</p>
              <div className="md:col-span-7">
                <h3 className="font-display text-h3 font-medium leading-tight mb-2">
                  <a href="#" className="hover:text-oxblood transition-colors">
                    Footnote
                  </a>
                </h3>
                <p className="text-body text-ink-70 leading-relaxed">
                  An epistemic infrastructure engine with a civic grounding
                  layer, built to translate information awareness into local
                  action.
                </p>
              </div>
              <div className="md:col-span-3 md:text-right">
                <p className="label-mono">In progress</p>
              </div>
            </div>
          </article>

          {/* Project 2 */}
          <article className="border-b border-ink-20 py-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline">
              <p className="label-mono md:col-span-2">No. 02 · 2026</p>
              <div className="md:col-span-7">
                <h3 className="font-display text-h3 font-medium leading-tight mb-2">
                  <a href="#" className="hover:text-oxblood transition-colors">
                    PostgreSQL Blog API
                  </a>
                </h3>
                <p className="text-body text-ink-70 leading-relaxed">
                  A Node.js / Express REST API migrated from MongoDB to
                  PostgreSQL with a relational schema, parameterized queries,
                  and clean CRUD on users and posts.
                </p>
              </div>
              <div className="md:col-span-3 md:text-right">
                <p className="label-mono">Shipped</p>
              </div>
            </div>
          </article>

          {/* Project 3 */}
          <article className="border-b border-ink-20 py-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline">
              <p className="label-mono md:col-span-2">No. 03 · planned</p>
              <div className="md:col-span-7">
                <h3 className="font-display text-h3 font-medium leading-tight mb-2">
                  <a href="#" className="hover:text-oxblood transition-colors">
                    Workshop OS
                  </a>
                </h3>
                <p className="text-body text-ink-70 leading-relaxed">
                  A small-batch manufacturing operations layer: CNC scheduling,
                  Theory-of-Constraints capacity planning, and on-chain
                  provenance for finished goods.
                </p>
              </div>
              <div className="md:col-span-3 md:text-right">
                <p className="label-mono">Concept</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* ===========================
          DISPATCHES PREVIEW
          =========================== */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-mono">Dispatches</p>
            <h2 className="font-display text-h2 leading-tight font-medium mt-3">
              Recent essays
            </h2>
          </div>
          <Link
            to="/blogs"
            className="label-mono hover:text-ink transition-colors hidden md:inline"
          >
            All dispatches →
          </Link>
        </div>

        <div className="border-t border-ink-20">
          <article className="border-b border-ink-20 py-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline">
              <p className="label-mono md:col-span-2">Apr 28, 2026</p>
              <div className="md:col-span-8">
                <h3 className="font-display text-h3 font-medium leading-tight mb-2">
                  <Link
                    to="/blogs/1"
                    className="hover:text-oxblood transition-colors"
                  >
                    Why I migrated my blog API from MongoDB to PostgreSQL
                  </Link>
                </h3>
                <p className="text-body text-ink-70 leading-relaxed">
                  On the case for relational integrity in even the smallest
                  projects, and what changed when I stopped using{" "}
                  <code className="font-mono text-small">.populate()</code>.
                </p>
              </div>
              <div className="md:col-span-2 md:text-right">
                <p className="label-mono">Tech</p>
              </div>
            </div>
          </article>

          <article className="border-b border-ink-20 py-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline">
              <p className="label-mono md:col-span-2">Apr 14, 2026</p>
              <div className="md:col-span-8">
                <h3 className="font-display text-h3 font-medium leading-tight mb-2">
                  <Link
                    to="/blogs/2"
                    className="hover:text-oxblood transition-colors"
                  >
                    Notes toward a civic grounding layer
                  </Link>
                </h3>
                <p className="text-body text-ink-70 leading-relaxed">
                  Translating awareness of public claims into action close to
                  home: the design problem that became Footnote.
                </p>
              </div>
              <div className="md:col-span-2 md:text-right">
                <p className="label-mono">Civic</p>
              </div>
            </div>
          </article>

          <article className="border-b border-ink-20 py-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline">
              <p className="label-mono md:col-span-2">Mar 30, 2026</p>
              <div className="md:col-span-8">
                <h3 className="font-display text-h3 font-medium leading-tight mb-2">
                  <Link
                    to="/blogs/3"
                    className="hover:text-oxblood transition-colors"
                  >
                    The garden as systems lab
                  </Link>
                </h3>
                <p className="text-body text-ink-70 leading-relaxed">
                  Eight pepper varieties, two pollinator-pair blueberries, and
                  one durable lesson about constraints.
                </p>
              </div>
              <div className="md:col-span-2 md:text-right">
                <p className="label-mono">Practice</p>
              </div>
            </div>
          </article>
        </div>
      </section>
    </>
  );
}
